import { useState } from 'react';
import { api } from '../api';
import { useUi } from '../context/UiContext';

const EMPTY = {
  brand_name: '',
  company_name: '',
  industry: '',
  website_url: '',
  primary_product: '',
  target_location: '',
  target_customer_type: '',
  ideal_budget: '',
  expected_timeline: '',
};

const REQUIRED = ['brand_name', 'industry', 'primary_product'];

const INDUSTRIES = [
  'Digital Marketing', 'Interior Design', 'Real Estate', 'Education', 'Healthcare',
  'Finance', 'Technology', 'Retail', 'Manufacturing', 'Food & Beverage',
  'Construction', 'Automotive', 'Fashion & Apparel', 'Consulting', 'Other',
];

export default function BrandModal({ open, onClose, onCreated }) {
  const [data, setData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const { showToast } = useUi();

  if (!open) return null;

  const set = (field) => (e) => setData((d) => ({ ...d, [field]: e.target.value }));

  const submit = async () => {
    const nextErrors = {};
    REQUIRED.forEach((f) => {
      if (!data[f]) nextErrors[f] = true;
    });

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      showToast('Please fill in all required fields.', 'error');
      return;
    }

    try {
      const brand = await api.createBrand(data);
      setData(EMPTY);
      setErrors({});
      showToast(`Brand "${brand.brand_name}" created!`, 'success');
      onCreated(brand);
    } catch (err) {
      showToast('Failed to create brand. Is the backend running?', 'error');
    }
  };

  const close = () => {
    setData(EMPTY);
    setErrors({});
    onClose();
  };

  return (
    <div className="modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <div className="modal-title">Create Brand</div>
            <div className="modal-subtitle">Set up a new brand profile to start tracking lead quality.</div>
          </div>
          <button className="modal-close" onClick={close}>✕</button>
        </div>

        <div className="modal-body">
          <div className="form-grid">
            <Field label="Brand Name" required error={errors.brand_name} span2>
              <input className="form-control" placeholder="e.g. Rush Republic" value={data.brand_name} onChange={set('brand_name')} />
            </Field>

            <Field label="Industry" required error={errors.industry} span2>
              <select className="form-control" value={data.industry} onChange={set('industry')}>
                <option value="">— Select Industry —</option>
                {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
              </select>
            </Field>

            <Field label="Primary Product / Service" required error={errors.primary_product} span2>
              <input className="form-control" placeholder="e.g. SEO, Branding, Interior Design" value={data.primary_product} onChange={set('primary_product')} />
            </Field>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={close}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>+ Create Brand</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, error, span2, children }) {
  return (
    <div className={`form-group${span2 ? ' col-span-2' : ''}${error ? ' has-error' : ''}`}>
      <label className="form-label">{label} {required && <span className="required">*</span>}</label>
      {children}
      {error && <span className="form-error" style={{ display: 'block' }}>This field is required.</span>}
    </div>
  );
}
